import React from "react";
import { FileType, getFileType } from "components/Launchables/utils";
import NotebookViewer from "./NotebookViewer";
import MarkdownRenderer from "./MarkdownViewer";
import { replaceBlobWithRaw } from "components/utils";
import { Badge } from "components/Graphs/Badge";
import { GitHubLogoIcon } from "@radix-ui/react-icons";
import { ArrowUpRight } from "lucide-react";

interface FileViewerProps {
  file?: string | null;
}

function getFileNameFromUrl(url: string): string {
  try {
    const pathParts = new URL(url).pathname.split("/").filter(Boolean);
    return pathParts[pathParts.length - 1] || "file";
  } catch {
    const pathParts = url.split("/").filter(Boolean);
    return pathParts[pathParts.length - 1] || "file";
  }
}

function getGitHubRepositoryLabel(url: string): string {
  try {
    const pathParts = new URL(url).pathname.split("/").filter(Boolean);
    const owner = pathParts[0];
    const repository = pathParts[1]?.replace(/\.git$/, "");
    return owner && repository ? `${owner}/${repository}` : "GitHub repository";
  } catch {
    return "GitHub repository";
  }
}

function getMarkdownReadmeUrl(url: string, type: FileType) {
  if (type === FileType.Markdown) {
    return replaceBlobWithRaw(url);
  }
  // Remove trailing slash and .git if present
  let normalizedUrl = url.replace(/\/+$/, "");
  if (normalizedUrl.endsWith(".git")) {
    normalizedUrl = normalizedUrl.slice(0, -4);
  }

  // Extract username and repo name
  const parts = normalizedUrl.split("/");
  const username = parts[parts.length - 2];
  const repoName = parts[parts.length - 1];

  // Construct markdown URL format (points directly to the README in the GitHub UI)
  //raw.githubusercontent.com/${username}/${repoName}/main/
  const baseUrl = `https://raw.githubusercontent.com/${username}/${repoName}/main/`;
  return baseUrl + "README.md";
}

const FileViewer: React.FC<FileViewerProps> = ({ file }) => {
  const fileType = getFileType(file);

  return (
    <div className="text-white">
      {/* Info text for file types */}
      {file &&
        (fileType === FileType.Colab || fileType === FileType.Notebook) && (
          <div className="mb-4">
            <p className="text-xs text-secondary">
              This jupyter notebook will be present in the instance after
              deployment
            </p>
          </div>
        )}

      {/* No file message */}
      {!file && (
        <>
          <hr className="w-full my-4 dark:border dark:border-zinc-800" />
          <div className="flex flex-col text-gray-900 dark:text-secondary">
            <p className="text-sm">
              This is a basic environment configuration with only hardware and
              container settings.
            </p>
            <p className="text-sm">No files or content were included.</p>
          </div>
        </>
      )}

      {/* Notebook/Colab viewer */}
      {file &&
        (fileType === FileType.Colab || fileType === FileType.Notebook) && (
          <>
            <hr className="w-full my-4 dark:border dark:border-zinc-800" />
            <div className="mb-4">
              <h1 className="text-lg font-semibold text-primary">
                {getFileNameFromUrl(file)}
              </h1>
            </div>
            <NotebookViewer file={file} />
          </>
        )}

      {/* GitHub repository badge */}
      {file && fileType === FileType.GithubRepo && (
        <a
          href={file}
          target="_blank"
          rel="noopener noreferrer"
          className="mb-4 inline-flex rounded-md outline-none focus-visible:ring-2 focus-visible:ring-highlight"
        >
          <Badge
            variant="neutral"
            className="gap-1.5 bg-black text-white ring-zinc-700 transition-colors hover:bg-zinc-900 hover:text-white dark:bg-black dark:text-white dark:ring-zinc-700"
          >
            <GitHubLogoIcon className="h-3.5 w-3.5" aria-hidden="true" />
            {getGitHubRepositoryLabel(file)}
            <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
          </Badge>
        </a>
      )}

      {/* README viewer */}
      {file &&
        (fileType === FileType.GithubRepo ||
          fileType === FileType.GitlabRepo ||
          fileType === FileType.Markdown) && (
          <MarkdownRenderer
            markdownUrl={getMarkdownReadmeUrl(file, fileType)}
          />
        )}

      {/* Unrecognized file type: show the invalid notebook fallback */}
      {file && fileType === FileType.Unknown && (
        <>
          <hr className="w-full my-4 dark:border dark:border-zinc-800" />
          <div className="mb-4">
            <h1 className="text-lg font-semibold text-primary">
              {getFileNameFromUrl(file)}
            </h1>
          </div>
          <NotebookViewer file={file} />
        </>
      )}
    </div>
  );
};

export default FileViewer;
